import React, { useEffect, useState } from 'react';
import {
  Activity,
  AlertCircle,
  BarChart3,
  CheckCircle,
  CreditCard,
  DollarSign,
  ExternalLink,
  Eye,
  Info,
  Link2,
  RefreshCw,
  ShoppingCart
} from 'lucide-react';
import { getPixelDiagnostics, META_PIXEL_ID } from '../../utils/metaPixel';
import './css/AdminMetaPixel.css';

const trackedEvents = [
  {
    name: 'PageView',
    label: 'Visite de page',
    description: 'Envoyé lors de la navigation sur les pages publiques de la boutique.',
    icon: Eye
  },
  {
    name: 'ViewContent',
    label: 'Consultation produit',
    description: "Envoyé quand un visiteur ouvre la fiche d'un produit.",
    icon: BarChart3
  },
  {
    name: 'AddToCart',
    label: 'Ajout au panier',
    description: "Envoyé après l'ajout réussi d'un produit au panier, avec sa valeur en TND.",
    icon: ShoppingCart
  },
  {
    name: 'InitiateCheckout',
    label: 'Début de commande',
    description: 'Envoyé lorsque le visiteur arrive au paiement avec un panier non vide.',
    icon: CreditCard
  },
  {
    name: 'Purchase',
    label: 'Achat confirmé',
    description: 'Envoyé uniquement après la création réussie de la commande, avec le total en TND.',
    icon: DollarSign
  }
];

const StatusPill = ({ ok, children }) => (
  <span className={`mp-status-pill ${ok ? 'is-ok' : 'is-warning'}`}>
    {ok ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
    {children}
  </span>
);

const AdminMetaPixel = () => {
  const [diagnostics, setDiagnostics] = useState(() => getPixelDiagnostics());
  const [lastCheck, setLastCheck] = useState(() => new Date());

  const refreshDiagnostics = () => {
    setDiagnostics(getPixelDiagnostics());
    setLastCheck(new Date());
  };

  useEffect(() => {
    const timer = window.setTimeout(refreshDiagnostics, 800);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="mp-page">
      <section className="mp-hero">
        <div>
          <div className="mp-eyebrow">
            <Activity size={17} /> Suivi marketing
          </div>
          <h1>Meta Pixel</h1>
          <p>
            Contrôlez la configuration du suivi Meta de Reyza et comprenez les actions
            mesurées sur la boutique.
          </p>
        </div>
        <StatusPill ok={diagnostics.configured}>Intégration configurée</StatusPill>
      </section>

      <section className="mp-summary-grid">
        <article className="mp-card mp-connection-card">
          <div className="mp-card-heading">
            <div className="mp-icon-box"><Link2 size={22} /></div>
            <div>
              <span className="mp-card-kicker">Connexion du site</span>
              <h2>Reyza est lié à un Pixel</h2>
            </div>
          </div>

          <div className="mp-id-row">
            <span>Meta Pixel ID</span>
            <code>{META_PIXEL_ID}</code>
          </div>

          <div className="mp-check-list">
            <div>
              <CheckCircle size={18} />
              <span>Code Pixel présent dans la version publique du site</span>
            </div>
            <div>
              <CheckCircle size={18} />
              <span>Événements e-commerce configurés en dinar tunisien (TND)</span>
            </div>
            <div>
              <CheckCircle size={18} />
              <span>Trafic administrateur exclu pour éviter de fausser les statistiques</span>
            </div>
          </div>
        </article>

        <article className="mp-card mp-runtime-card">
          <div className="mp-card-heading">
            <div className="mp-icon-box"><Activity size={22} /></div>
            <div>
              <span className="mp-card-kicker">Diagnostic local</span>
              <h2>Page administrateur</h2>
            </div>
          </div>

          <div className="mp-runtime-state">
            <StatusPill ok={false}>Pixel désactivé ici volontairement</StatusPill>
            <p>
              Le Pixel ne se charge pas dans l'administration. Il fonctionne sur les pages
              publiques visitées par les clients.
            </p>
          </div>

          <button type="button" className="mp-refresh-button" onClick={refreshDiagnostics}>
            <RefreshCw size={16} /> Actualiser le diagnostic
          </button>
          <small>Dernière vérification : {lastCheck.toLocaleTimeString('fr-FR')}</small>
        </article>
      </section>

      <section className="mp-section">
        <div className="mp-section-title">
          <div>
            <span className="mp-card-kicker">Événements actifs</span>
            <h2>Ce que Reyza envoie à Meta</h2>
          </div>
          <span className="mp-event-count">{trackedEvents.length} événements</span>
        </div>

        <div className="mp-events-grid">
          {trackedEvents.map(({ name, label, description, icon: Icon }) => (
            <article className="mp-event-card" key={name}>
              <div className="mp-event-icon"><Icon size={21} /></div>
              <div>
                <div className="mp-event-header">
                  <h3>{label}</h3>
                  <span>Configuré</span>
                </div>
                <code>{name}</code>
                <p>{description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mp-info-grid">
        <article className="mp-card">
          <div className="mp-card-heading">
            <div className="mp-icon-box"><Info size={22} /></div>
            <div>
              <span className="mp-card-kicker">Rôle du Pixel</span>
              <h2>Ce qu'il fait</h2>
            </div>
          </div>
          <ul className="mp-simple-list">
            <li>Mesure les visites et les actions importantes de la boutique.</li>
            <li>Aide Meta à attribuer les ventes aux publicités Facebook et Instagram.</li>
            <li>Permet de créer des audiences et d'optimiser les campagnes publicitaires.</li>
          </ul>
        </article>

        <article className="mp-card">
          <div className="mp-card-heading">
            <div className="mp-icon-box"><AlertCircle size={22} /></div>
            <div>
              <span className="mp-card-kicker">Limites</span>
              <h2>Ce qu'il ne fait pas</h2>
            </div>
          </div>
          <ul className="mp-simple-list">
            <li>Il ne publie et ne gère pas les posts Facebook ou Instagram.</li>
            <li>Il ne crée pas les publicités ou les campagnes.</li>
            <li>Les chiffres Meta restent dans Events Manager sans connexion API privée.</li>
          </ul>
        </article>
      </section>

      <section className="mp-card mp-test-card">
        <div className="mp-test-content">
          <div className="mp-icon-box"><CheckCircle size={22} /></div>
          <div>
            <span className="mp-card-kicker">Vérification finale</span>
            <h2>Tester les événements dans Meta</h2>
            <ol>
              <li>Ouvrez Events Manager et sélectionnez le Pixel {META_PIXEL_ID}.</li>
              <li>Choisissez « Tester les événements » puis saisissez https://reyza.tn.</li>
              <li>Consultez un produit, ajoutez-le au panier et effectuez une commande test.</li>
              <li>Vérifiez que chaque événement apparaît en temps réel sans erreur.</li>
            </ol>
          </div>
        </div>
        <div className="mp-actions">
          <a href="https://reyza.tn" target="_blank" rel="noreferrer" className="mp-button mp-button-secondary">
            Ouvrir la boutique <ExternalLink size={16} />
          </a>
          <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noreferrer" className="mp-button mp-button-primary">
            Ouvrir Events Manager <ExternalLink size={16} />
          </a>
        </div>
      </section>

      <section className="mp-note">
        <AlertCircle size={20} />
        <div>
          <strong>Suivi actuel : navigateur uniquement</strong>
          <p>
            La Conversions API côté serveur n'est pas encore configurée. Elle est optionnelle
            et nécessite un jeton d'accès généré par le propriétaire dans Meta Events Manager.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AdminMetaPixel;
